import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SearchTracking from '../../components/tracking/SearchTracking';
import TrackingHeader from '../../components/tracking/TrackingHeader';
import TrackingDetails from '../../components/tracking/TrackingDetails';
import TrackingTimeline from '../../components/tracking/TrackingTimeline';
import CommentsSection from '../../components/tracking/CommentsSection';
import TrackingAttachmentsSection from '../../components/tracking/TrackingAttachmentsSection';
import RatingBanner from '../../components/rating/RatingBanner';
import claimsService from '../../services/claimService';
import { toast } from 'react-toastify';

const TrackingSystem = () => {
    // Récupérer le paramètre trackingNumber de l'URL
    const { trackingNumber: urlTrackingNumber } = useParams();
    const navigate = useNavigate();

    const [found, setFound] = useState(false);
    const [showError, setShowError] = useState(false);
    const [claimData, setClaimData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [addingComment, setAddingComment] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [originalApiData, setOriginalApiData] = useState(null);

    // Cette fonction convertit les données brutes de l'API en format adapté pour l'affichage
    const formatClaimData = (apiData) => {
        if (!apiData) return null;

        console.log("Formatage des données de réclamation:", apiData);

        // Déterminer si le statut permet d'ajouter des commentaires/pièces jointes
        const canInteract = !['closed', 'rejected'].includes(apiData.status);

        // Vérifier si la réclamation peut être notée (clôturée mais pas encore notée)
        const canRate = apiData.status === 'closed' && (!apiData.rating || !apiData.rating.id);

        // Vérifier si la réclamation a déjà été notée
        const isRated = apiData.rating && apiData.rating.id;

        // Générer la timeline à partir des données d'historique
        const generateTimeline = () => {
            // Si le statut d'historique est disponible, l'utiliser
            if (apiData.statusHistory && apiData.statusHistory.length > 0) {
                const sortedHistory = [...apiData.statusHistory].sort((a, b) =>
                    new Date(a.createdAt) - new Date(b.createdAt)
                );

                return sortedHistory.map(historyItem => ({
                    nom: historyItem.transitionName || `Transition vers ${historyItem.newStatus}`,
                    status: historyItem.newStatus === apiData.status ? 'in_progress' : 'completed',
                    date: new Date(historyItem.createdAt).toLocaleDateString('fr-FR'),
                    description: historyItem.comment || 'Traitement de votre dossier',
                    assigneA: {
                        nom: historyItem.performedByName || 'Agent',
                        service: 'Service Réclamations',
                        commentaire: historyItem.comment || 'Traitement en cours'
                    }
                }));
            }

            // Fallback simple si pas d'historique
            return [
                {
                    nom: "Réception",
                    status: "completed",
                    date: new Date(apiData.details?.createdAt).toLocaleDateString('fr-FR'),
                    description: "Réclamation enregistrée dans le système",
                    assigneA: {
                        nom: "Agent d'accueil",
                        service: "Service Client",
                        commentaire: "Dossier reçu et enregistré"
                    }
                },
                // {
                //     nom: apiData.status || "En cours",
                //     status: "in_progress",
                //     date: new Date().toLocaleDateString('fr-FR'),
                //     description: "En cours de traitement",
                //     assigneA: {
                //         nom: "Service Réclamations",
                //         service: "Service Réclamations",
                //         commentaire: "Analyse en cours"
                //     }
                // }
            ];
        };

        // Format final des données
        return {
            id: apiData.id || apiData.trackingNumber,
            trackingNumber: apiData.trackingNumber,
            status: apiData.status,
            creation: new Date(apiData.details?.createdAt).toLocaleDateString('fr-FR'),
            element: apiData.details?.element,
            categorie: apiData.details?.categorie,
            description: apiData.details?.description,
            pointService: apiData.details?.pointService,
            attente: apiData.details?.attente,
            details: apiData.details,

            // Informations sur le SLA
            duree: apiData.slaTrackings && apiData.slaTrackings.length > 0
                ? `${apiData.slaTrackings[0].durationHours} heures`
                : "Variable",

            // Responsables (valeurs par défaut)
            responsable: apiData.assignments && apiData.assignments.length > 0
                ? apiData.assignments[0].assignedToName
                : "Service Réclamations",
            serviceResponsable: "Service Réclamations",
            contactResponsable: {
                email: "reclamations@banque.com",
                telephone: "+221 33 839 03 03"
            },

            // Timeline et workflow
            workflow: {
                etapes: generateTimeline(),
                comments: apiData.comments || []
            },

            // Attributs d'interaction
            canAddComment: canInteract,
            canAddAttachment: canInteract,
            canRate: canRate,
            isRated: isRated,
            rating: apiData.rating
        };
    };

    const handleAddComment = async (text) => {
        if (!text.trim() || !claimData?.trackingNumber) return;

        setAddingComment(true);
        try {
            await claimsService.addComment(
                claimData.trackingNumber,
                text,
                originalApiData,
                false,           // Non interne
                "Client"         // Rôle défini comme Client
            );

            // Recharger les données
            const updatedClaim = await claimsService.getClaimByTrackingNumber(claimData.trackingNumber);
            setOriginalApiData(updatedClaim);
            setClaimData(formatClaimData(updatedClaim));

            toast.success('Commentaire ajouté avec succès');
        } catch (error) {
            console.error('Erreur lors de l\'ajout du commentaire:', error);
            toast.error(error.message || 'Erreur lors de l\'ajout du commentaire');
        } finally {
            setAddingComment(false);
        }
    };

    const handleSearch = async (searchTrackingNumber) => {
        if (!searchTrackingNumber || searchTrackingNumber.trim() === '') {
            toast.error('Veuillez saisir un numéro de suivi valide');
            return;
        }

        setLoading(true);
        try {
            // Mettre à jour le trackingNumber avec celui recherché
            setTrackingNumber(searchTrackingNumber);

            // Mettre à jour l'URL sans recharger la page
            navigate(`/tracking/${searchTrackingNumber}`, { replace: true });

            const response = await claimsService.getClaimByTrackingNumber(searchTrackingNumber);
            setOriginalApiData(response);

            const formattedData = formatClaimData(response);
            setClaimData(formattedData);
            setFound(true);
            setShowError(false);

            console.log('Recherche réussie - trackingNumber:', searchTrackingNumber);
        } catch (error) {
            console.error('Erreur lors de la recherche:', error);
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleAttachmentsUpdated = async () => {
        try {
            // Recharger les données du dossier pour avoir les pièces jointes à jour
            const updatedClaim = await claimsService.getClaimByTrackingNumber(trackingNumber);
            setOriginalApiData(updatedClaim);
            setClaimData(formatClaimData(updatedClaim));
            toast.success('Pièces jointes mises à jour');
        } catch (error) {
            console.error('Erreur lors du rechargement des données:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Effet pour charger automatiquement les données si un numéro de suivi est fourni dans l'URL
    useEffect(() => {
        if (urlTrackingNumber) {
            setTrackingNumber(urlTrackingNumber);
            handleSearch(urlTrackingNumber);
        }
    }, [urlTrackingNumber]);

    if (loading) {
        return <div className="d-flex justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
            </div>
        </div>;
    }

    if (!found) {
        return <SearchTracking onSearch={handleSearch} showError={showError} />;
    }

    return (
        <div className="container py-5">

            {(claimData?.canRate || claimData?.isRated) && (
                <RatingBanner
                    trackingNumber={claimData.trackingNumber}
                    isRated={claimData.isRated}
                    rating={claimData.rating}
                />
            )}

            <TrackingHeader
                status={claimData?.status}
                onBack={() => {
                    setFound(false);
                    navigate('/tracking', { replace: true });
                }}
            />

            <TrackingDetails
                details={claimData}
                contactInfo={claimData}
            />

            {claimData?.canAddAttachment && (
                <TrackingAttachmentsSection
                    trackingNumber={claimData.trackingNumber}
                    onAttachmentsUpdated={handleAttachmentsUpdated}
                />
            )}

            <TrackingTimeline
                etapes={claimData?.workflow?.etapes || []}
            />

            {claimData?.canAddComment ? (
                <CommentsSection
                    comments={claimData?.workflow?.comments || []}
                    onAddComment={handleAddComment}
                    isSubmitting={addingComment}
                    trackingNumber={claimData.trackingNumber}
                />
            ) : (
                <div className="alert alert-info mt-4">
                    <p className="mb-0">
                        Cette réclamation est clôturée. Vous ne pouvez plus ajouter de commentaires.
                        {claimData?.canRate && (
                            <span> N'oubliez pas d'évaluer notre service en utilisant le bouton "Évaluer" ci-dessus.</span>
                        )}
                        {!claimData?.canRate && !claimData?.isRated && (
                            <span> Si vous avez besoin d'informations supplémentaires, merci de contacter notre service client.</span>
                        )}
                    </p>
                </div>
            )}
        </div>
    );
};

export default TrackingSystem;